import React from 'react';
import i18n from '@/i18n/config';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea
} from '@heroui/react';
import { toast } from 'react-toastify';

import type { ExperimentItem } from '@/types';
import { getStatusLabel, type ExperimentStatus } from '@/utils/experiments';
import { useAtom } from 'jotai';
import { setExperimentStatus } from '@/store';

const UNBLOCK_TARGETS: ExperimentStatus[] = ['draft', 'cancelled'];
const MAX_REASON_LENGTH = 2048;

interface UnblockExperimentModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  experiment: ExperimentItem;
  onSuccess?: () => void;
}

export function UnblockExperimentModal({
  isOpen,
  onOpenChange,
  experiment,
  onSuccess
}: UnblockExperimentModalProps) {
  const [selectedStatus, setSelectedStatus] = React.useState<ExperimentStatus>('draft');
  const [cancellationReason, setCancellationReason] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [, setStatusAtom] = useAtom(setExperimentStatus);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedStatus('draft');
      setCancellationReason('');
    }
  }, [isOpen]);

  const needsReason = selectedStatus === 'cancelled';
  const canSubmit = !needsReason || cancellationReason.trim().length > 0;

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit) return;
    try {
      setIsSubmitting(true);
      await setStatusAtom({
        id: experiment.id,
        status: selectedStatus,
        ...(needsReason && { cancellation_reason: cancellationReason.trim() })
      });
      toast.success(`Experiment unblocked and set to ${getStatusLabel(selectedStatus)}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to unblock experiment:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedStatus, cancellationReason, needsReason, canSubmit, experiment.id, setStatusAtom, onOpenChange, onSuccess]);

  const handleClose = React.useCallback(() => {
    setSelectedStatus('draft');
    setCancellationReason('');
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Unblock Experiment</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <p className="text-default-600 text-sm">{i18n.t('copy.unblock')}<span className="font-semibold">{experiment.name}</span> and restore it to a new status.
                </p>
                <Select
                  label={i18n.t('copy.set_status_to')}
                  placeholder={i18n.t('copy.select_status')}
                  selectedKeys={[selectedStatus]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as ExperimentStatus;
                    setSelectedStatus(selected);
                  }}
                  variant="bordered"
                >
                  {UNBLOCK_TARGETS.map((status) => (
                    <SelectItem key={status}>{getStatusLabel(status)}</SelectItem>
                  ))}
                </Select>
                {needsReason && (
                  <Textarea
                    label={i18n.t('copy.cancellation_reason_required')}
                    placeholder={i18n.t('copy.why_is_this_experiment_being_cancelled')}
                    value={cancellationReason}
                    onValueChange={setCancellationReason}
                    variant="bordered"
                    minRows={2}
                    maxLength={MAX_REASON_LENGTH}
                    description={`${cancellationReason.length}/${MAX_REASON_LENGTH} characters`}
                  />
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isSubmitting}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit} isDisabled={!canSubmit}>{i18n.t('copy.unblock')}</Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
